import { useEffect, useMemo, useState } from 'react';

import { GroupInfo } from '../interfaces/GroupInfo';
import { Option } from '../../general-types';
import { customerGroupService } from '../services/customerGroupService';
import { addAllOption, transformToOptions } from '../../shared/utils/arrayUtils';

type UseGroupListReturn = {
  groupList: GroupInfo[];
  groupListOption: Option[];
  groupListOptionWithAll: Option[];
};

type UseGroupListProps = {
  onlyActive?: boolean;
  isRunGlobalSpinner?: boolean;
  /** use to filter groups by entityId (for support super admin) */
  entityId?: number;
};

export const useGroupList = ({
  onlyActive = false,
  entityId,
  isRunGlobalSpinner,
}: UseGroupListProps = {}): UseGroupListReturn => {
  const [groupList, setGroupList] = useState<GroupInfo[]>([]);

  const initGroupList = async () => {
    const { groups } = await customerGroupService.getGroupList(isRunGlobalSpinner);

    if (!onlyActive && entityId === undefined) {
      setGroupList(groups);
      return;
    }

    const filtered = groups.filter((group) => {
      if (onlyActive && !group.active) {
        return false;
      }

      if (entityId !== undefined && group.entityId !== entityId) {
        return false;
      }

      return true;
    });

    setGroupList(filtered);
  };

  useEffect(() => {
    initGroupList();
  }, []);

  const groupListOption = useMemo(
    () => transformToOptions({ list: groupList, labelKey: 'name', valueKey: 'id' }),
    [groupList],
  );

  const groupListOptionWithAll = useMemo(
    () => addAllOption(groupListOption),
    [groupListOption],
  );

  return { groupList, groupListOption, groupListOptionWithAll };
};
